/* eslint-disable @typescript-eslint/no-explicit-any */
import type React from 'react';
import {
    useCallback,
    useContext,
    useState,
} from 'react';
import { useForm } from 'react-hook-form';
import {
    Link,
    useNavigate,
} from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import {
    ArrowLeft,
    EyeIcon,
    EyeOffIcon,
    LockKeyhole,
    Mail,
} from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import AuthContext from '@/contexts/auth';
import { baseRequest } from '@/lib/base';
import { toastApiError } from '@/lib/toast-error';
import {
    clearLocalStorage,
    cn,
    readLocalStorage,
    setLocalStorage,
} from '@/lib/utils';

interface LoginResponse {
  ok: boolean;
  data: {
    tokens: {
      access: string;
      refresh: string;
    };
    user: {
      id: number;
      uuid: string;
      email: string;
      owner_full_name: string;
      business_name?: string;
      profile_image?: string | null;
      phone_number?: string;
      is_active?: boolean;
    };
    detail?: string;
  };
  message?: string;
  status: number;
  statusText: string;
}

interface ApiError {
  message: string;
}

const login = async (body: any) => {
    const response = await baseRequest({
        url: '/system/auth/login/',
        method: 'POST',
        body: JSON.stringify(body),
        contentType: 'application/json',
    });
    return response;
};

const loginSchema = z.object({
    email: z.string().email().min(1, 'Invalid email address'),
    password: z.string().min(1, 'Password must be at least 6 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>

const REMEMBERED_CREDENTIALS_KEY = 'chanco-remembered-credentials';

function getSavedCredentials(): { email: string; password: string } | null {
    const raw = readLocalStorage(REMEMBERED_CREDENTIALS_KEY);
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        if (parsed?.email && parsed?.password) return parsed;
    } catch { /* ignore */ }
    return null;
}

export default function Login({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
    const saved = getSavedCredentials();
    const navigate = useNavigate();
    const [isVisible, setIsVisible] = useState(false);
    const [rememberMe, setRememberMe] = useState<boolean>(true);
    const { login: userLogin } = useContext(AuthContext);
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<LoginFormData>({
        resolver: zodResolver(loginSchema),
        defaultValues: saved ?? undefined,
    });

    const togglePasswordVisibility = useCallback(() => {
        setIsVisible((prev) => !prev);
    }, []);

    const loginMutation = useMutation<LoginResponse, ApiError, LoginFormData>({
        mutationFn: login,
        onSuccess: (data, variables) => {
            if (data?.ok) {
                // Pass both tokens and user data to the updated login function
                userLogin(
                    {
                        access: data.data.tokens.access,
                        refresh: data.data.tokens.refresh,
                    },
                    data.data.user,
                    rememberMe,
                );
                toast.success(data.message || 'Login successful');
            } else {
                const detail = (data.data?.detail || '').toLowerCase();
                if (detail.includes('email') && detail.includes('not verified')) {
                    toast.info('Please verify your email to continue');
                    navigate('/verify-otp', { state: { email: variables.email, flow: 'registration' } });
                } else {
                    clearLocalStorage(REMEMBERED_CREDENTIALS_KEY);
                    toastApiError(data.data, 'Login failed');
                }
            }
        },
        onError: (error) => {
            clearLocalStorage(REMEMBERED_CREDENTIALS_KEY);
            toast.error(error.message || 'An error occurred during login');
        },
    });

    const onSubmit = (formData: LoginFormData) => {
        // Save or clear credentials before mutating so the flag is current
        if (rememberMe) {
            setLocalStorage(
                REMEMBERED_CREDENTIALS_KEY,
                JSON.stringify({ email: formData.email, password: formData.password }),
            );
        } else {
            clearLocalStorage(REMEMBERED_CREDENTIALS_KEY);
        }
        loginMutation.mutate(formData);
    };

    return (
        <div className="min-h-screen bg-background flex flex-col">
            {/* Top nav */}
            <div className="p-6">
                <Link
                    to="/"
                    className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Home
                </Link>
            </div>

            <div className="flex-1 flex items-center justify-center px-4 pb-12">
                <div className={cn('flex flex-col gap-6 w-full max-w-sm', className)} {...props}>
                    {/* Logo and header */}
                    <div className="flex flex-col items-center gap-3">
                        <Link to="/">
                            <img
                                src="/chanco-vertical.png"
                                alt="Chanco"
                                className="h-24 w-auto"
                            />
                        </Link>
                        <h1 className="text-2xl font-bold text-foreground">Login</h1>
                        <p className="text-sm text-muted-foreground">
                            Please enter your details to login
                        </p>
                    </div>

                    <Card className="border-0 shadow-lg">
                        <CardContent className="pt-6">
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <div className="grid gap-5">
                                    {/* Email field */}
                                    <div className="grid gap-2">
                                        <Label htmlFor="email">Email</Label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                id="email"
                                                type="text"
                                                placeholder="m@example.com"
                                                {...register('email')}
                                                className={cn(
                                                    'pl-10',
                                                    { 'focus-visible:ring-destructive': errors.email },
                                                )}
                                            />
                                        </div>
                                        {errors.email && <span className="text-sm text-destructive">{errors.email.message}</span>}
                                    </div>

                                    {/* Password field */}
                                    <div className="grid gap-2">
                                        <Label htmlFor="password">Password</Label>
                                        <div className="relative">
                                            <LockKeyhole className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                id="password"
                                                type={isVisible ? 'text' : 'password'}
                                                placeholder="Enter your password"
                                                {...register('password')}
                                                className={cn(
                                                    'pl-10 pr-10',
                                                    { 'focus-visible:ring-destructive': errors.password },
                                                )}
                                            />
                                            <button
                                                type="button"
                                                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground focus:outline-none"
                                                onClick={togglePasswordVisibility}
                                                aria-label={isVisible ? 'Hide password' : 'Show password'}
                                            >
                                                {isVisible ? <EyeIcon className="h-4 w-4" /> : <EyeOffIcon className="h-4 w-4" />}
                                            </button>
                                        </div>
                                        {errors.password && <span className="text-sm text-destructive">{errors.password.message}</span>}
                                    </div>

                                    {/* Remember me + Forgot password */}
                                    <div className="flex items-center justify-between">
                                        <label className="flex items-center gap-2 cursor-pointer" htmlFor="remember-me">
                                            <input
                                                id="remember-me"
                                                type="checkbox"
                                                checked={rememberMe}
                                                onChange={(e) => setRememberMe(e.target.checked)}
                                                className="h-4 w-4 rounded border-input accent-primary"
                                            />
                                            <span className="text-sm text-muted-foreground">Remember me</span>
                                        </label>
                                        <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                                            Forgot password?
                                        </Link>
                                    </div>

                                    {/* Login button */}
                                    <Button
                                        type="submit"
                                        className="w-full"
                                        disabled={loginMutation.isPending}
                                    >
                                        {loginMutation.isPending ? 'Logging in...' : 'Login'}
                                    </Button>

                                    {/* Register link */}
                                    <p className="text-center text-sm text-muted-foreground">
                                        Don&apos;t have an account yet?
                                        {' '}
                                        <Link to="/signup" className="text-primary font-medium hover:underline">
                                            Register
                                        </Link>
                                    </p>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
